import type { EChartsType } from "echarts";
import ReactECharts from "echarts-for-react";
import CloseIcon from "@mui/icons-material/Close";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import {
  Box,
  Dialog,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { getThemeConfig } from "@/config/themes";
import { CHART_TABLE_PANEL_HEIGHT_PX } from "@/constants";
import { useResolvedThemeMode } from "@/features/appearance/hooks/useResolvedThemeMode";
import { buildChartOption, getAvailableChartTypes } from "@/features/chat/lib/chartOptions";
import type { ChartType, GeneralizedChartDefinition } from "@/features/chat/model/chatTypes";
import { ChartTypeSelector } from "@/features/chat/ui/ChartTypeSelector";

type ChartPanelProps = {
  charts: GeneralizedChartDefinition[];
  data: Record<string, unknown>[];
  onAddToApp?: (chart: GeneralizedChartDefinition) => void;
  showTitle?: boolean;
  /**
   * Saved chart-type preference that overrides each chart's
   * `recommended_chart_type` as the initially selected type. Applies to every
   * chart the panel renders (callers that persist a preference render exactly
   * one chart per panel).
   */
  preferredChartType?: ChartType | null;
  /**
   * Called when the user picks a chart type. Provide this only where the
   * selection should persist (App Studio builder); omit it where switching is
   * view-local (published apps, chat responses).
   */
  onChartTypeChange?: (chart: GeneralizedChartDefinition, chartType: ChartType) => void;
};

type ChartDownloadFormat = "png" | "jpeg";

/**
 * The initially selected chart type: the saved preference when it's still one
 * of the chart's currently available types, otherwise the chart definition's
 * recommendation. A stale preference (e.g. the data shape changed so the type
 * is no longer offered) falls back rather than rendering an unavailable type.
 */
function resolveInitialChartType(
  chart: GeneralizedChartDefinition,
  preferredChartType: ChartType | null | undefined,
  availableTypes: ChartType[],
): ChartType {
  if (preferredChartType && availableTypes.includes(preferredChartType)) {
    return preferredChartType;
  }
  return chart.recommended_chart_type;
}

function sanitizeFilename(value: string): string {
  const trimmed = value.trim().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
  return trimmed || "chart";
}

function downloadChartImage(
  instance: EChartsType | undefined,
  options: {
    format: ChartDownloadFormat;
    filename: string;
    backgroundColor: string;
  },
): void {
  if (!instance) {
    return;
  }
  const { format, filename, backgroundColor } = options;
  const extension = format === "jpeg" ? "jpg" : "png";
  const url = instance.getDataURL({
    type: format,
    pixelRatio: 2,
    backgroundColor,
  });
  const link = document.createElement("a");
  link.href = url;
  link.download = `${sanitizeFilename(filename)}.${extension}`;
  link.click();
}

type ChartHeaderProps = {
  chart: GeneralizedChartDefinition;
  titleVariant: "body2" | "h6";
  trailingActions: React.ReactNode;
  showTitle?: boolean;
};

function ChartHeader({ chart, titleVariant, trailingActions, showTitle = true }: ChartHeaderProps) {
  const chartTitle = chart.title ?? chart.id;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1,
        flexWrap: "wrap",
      }}
    >
      {showTitle && chart.title ? (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant={titleVariant} sx={{ fontWeight: 600 }}>
            {chartTitle}
          </Typography>
          {chart.description ? (
            <Typography
              variant="caption"
              sx={{ display: "block", color: "var(--theme-text-secondary)" }}
            >
              {chart.description}
            </Typography>
          ) : null}
        </Box>
      ) : (
        <Box sx={{ flex: 1 }} />
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0, flexWrap: "nowrap" }}>
        {trailingActions}
      </Box>
    </Box>
  );
}

type ChartCardProps = {
  chart: GeneralizedChartDefinition;
  data: Record<string, unknown>[];
  palette: string[];
  height: number;
  onAddToApp?: (chart: GeneralizedChartDefinition) => void;
  showTitle?: boolean;
  preferredChartType?: ChartType | null;
  onChartTypeChange?: (chart: GeneralizedChartDefinition, chartType: ChartType) => void;
};

function ChartCard({
  chart,
  data,
  palette,
  height,
  onAddToApp,
  showTitle = true,
  preferredChartType,
  onChartTypeChange,
}: ChartCardProps) {
  const { t } = useTranslation("chat");
  const themeMode = useResolvedThemeMode();
  const themeColors = getThemeConfig(themeMode).colors;
  const surfaceColor = themeColors.surface;
  const textColors = useMemo(
    () => ({ secondary: themeColors.textSecondary, disabled: themeColors.textDisabled }),
    [themeColors.textSecondary, themeColors.textDisabled],
  );
  const inlineRef = useRef<ReactECharts>(null);
  const expandedRef = useRef<ReactECharts>(null);
  const [expanded, setExpanded] = useState(false);
  const [downloadAnchor, setDownloadAnchor] = useState<HTMLElement | null>(null);
  const availableTypes = useMemo(() => getAvailableChartTypes(chart), [chart]);
  const [selectedChartType, setSelectedChartType] = useState<ChartType>(() =>
    resolveInitialChartType(chart, preferredChartType, availableTypes),
  );
  // Splitting into two y-axes (see buildChartOption) only happens with
  // exactly two measures, so hiding an axis when its measure is deselected
  // via the legend only ever makes sense in that shape too.
  const isDualAxisEligible = chart.measures.length === 2;
  const [hiddenMeasureKeys, setHiddenMeasureKeys] = useState<Set<string>>(() => new Set());

  const handleChartTypeChange = useCallback(
    (nextChartType: ChartType) => {
      setSelectedChartType(nextChartType);
      onChartTypeChange?.(chart, nextChartType);
    },
    [chart, onChartTypeChange],
  );
  const option = useMemo(
    () => buildChartOption(chart, data, selectedChartType, palette, textColors, hiddenMeasureKeys),
    [chart, data, selectedChartType, palette, textColors, hiddenMeasureKeys],
  );

  const handleLegendSelectChanged = useCallback(
    (params: { selected: Record<string, boolean> }) => {
      // A stacked chart names each series "<stack value> (<measure label>)",
      // so a measure only counts as hidden once every one of its stack
      // values has been deselected, not just one.
      const stacked = Boolean(chart.stack_dimension);
      const nextHidden = new Set<string>();
      chart.measures.forEach((measure) => {
        const label = measure.label ?? measure.key;
        const relatedEntries = Object.entries(params.selected).filter(([name]) =>
          stacked ? name.endsWith(`(${label})`) : name === label,
        );
        if (relatedEntries.length > 0 && relatedEntries.every(([, isSelected]) => !isSelected)) {
          nextHidden.add(measure.key);
        }
      });
      setHiddenMeasureKeys(nextHidden);
    },
    [chart],
  );
  const chartEvents = useMemo(
    () => (isDualAxisEligible ? { legendselectchanged: handleLegendSelectChanged } : undefined),
    [isDualAxisEligible, handleLegendSelectChanged],
  );

  const downloadFilename = chart.title ?? chart.id;

  const resizeExpandedChart = useCallback(() => {
    requestAnimationFrame(() => {
      expandedRef.current?.getEchartsInstance()?.resize();
    });
  }, []);

  useEffect(() => {
    if (!expanded) {
      return;
    }
    resizeExpandedChart();
  }, [expanded, resizeExpandedChart, option]);

  const handleDownload = (format: ChartDownloadFormat, source: "inline" | "expanded") => {
    const instance =
      source === "expanded"
        ? expandedRef.current?.getEchartsInstance()
        : inlineRef.current?.getEchartsInstance();
    downloadChartImage(instance, {
      format,
      filename: downloadFilename,
      backgroundColor: surfaceColor,
    });
    setDownloadAnchor(null);
  };

  const openDownloadMenu = (event: MouseEvent<HTMLElement>) => {
    setDownloadAnchor(event.currentTarget);
  };

  const trailingActions = (source: "inline" | "expanded") => (
    <>
      <ChartTypeSelector
        value={selectedChartType}
        availableTypes={availableTypes}
        onChange={handleChartTypeChange}
      />
      {onAddToApp ? (
        <Tooltip title={t("response.charts.addToApp")}>
          <IconButton
            size="small"
            aria-label={t("response.charts.addToApp")}
            onClick={() => onAddToApp(chart)}
            sx={{ color: "var(--theme-text-secondary)" }}
          >
            <DashboardCustomizeOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      {source === "inline" ? (
        <Tooltip title={t("response.charts.expand")}>
          <IconButton
            size="small"
            aria-label={t("response.charts.expand")}
            onClick={() => setExpanded(true)}
            sx={{ color: "var(--theme-text-secondary)" }}
          >
            <FullscreenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      <Tooltip title={t("response.charts.download")}>
        <IconButton
          size="small"
          aria-label={t("response.charts.download")}
          onClick={openDownloadMenu}
          sx={{ color: "var(--theme-text-secondary)" }}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <ChartHeader
          chart={chart}
          titleVariant="body2"
          trailingActions={trailingActions("inline")}
          showTitle={showTitle}
        />
        <ReactECharts
          ref={inlineRef}
          option={option}
          style={{ width: "100%", height }}
          notMerge
          lazyUpdate
          onEvents={chartEvents}
        />
      </Box>

      <Menu
        anchorEl={downloadAnchor}
        open={Boolean(downloadAnchor)}
        onClose={() => setDownloadAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => handleDownload("png", expanded ? "expanded" : "inline")}>
          {t("response.charts.downloadPng")}
        </MenuItem>
        <MenuItem onClick={() => handleDownload("jpeg", expanded ? "expanded" : "inline")}>
          {t("response.charts.downloadJpg")}
        </MenuItem>
      </Menu>

      <Dialog fullScreen open={expanded} onClose={() => setExpanded(false)}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            bgcolor: "var(--theme-surface)",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: "1px solid var(--theme-border)",
            }}
          >
            <ChartHeader
              chart={chart}
              titleVariant="h6"
              showTitle={showTitle}
              trailingActions={
                <>
                  {trailingActions("expanded")}
                  <Tooltip title={t("response.charts.close")}>
                    <IconButton
                      aria-label={t("response.charts.close")}
                      onClick={() => setExpanded(false)}
                      sx={{ color: "var(--theme-text-secondary)" }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </>
              }
            />
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, p: 2 }}>
            <ReactECharts
              ref={expandedRef}
              option={option}
              style={{ width: "100%", height: "100%" }}
              notMerge
              lazyUpdate
              onChartReady={resizeExpandedChart}
              onEvents={chartEvents}
            />
          </Box>
        </Box>
      </Dialog>
    </>
  );
}

export function ChartPanel({
  charts,
  data,
  onAddToApp,
  showTitle = true,
  preferredChartType,
  onChartTypeChange,
}: ChartPanelProps) {
  const themeMode = useResolvedThemeMode();
  const palette = getThemeConfig(themeMode).colors.chartPalette;

  if (charts.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {charts.map((chart) => (
        <ChartCard
          key={chart.id}
          chart={chart}
          data={data}
          palette={palette}
          height={CHART_TABLE_PANEL_HEIGHT_PX}
          onAddToApp={onAddToApp}
          showTitle={showTitle}
          preferredChartType={preferredChartType}
          onChartTypeChange={onChartTypeChange}
        />
      ))}
    </Box>
  );
}
